import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import JuntoApiService, { ClassCategory } from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const ClassCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<ClassCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ClassCategory | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_type: 'group' as 'group' | 'private' | 'event' | 'wellness',
  });

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await JuntoApiService.getClassCategories(includeInactive);
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openCreateDialog = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      event_type: 'group',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (category: ClassCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      event_type: category.event_type,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      const categoryData = {
        ...formData,
        id: editingCategory?.id || `category-${Date.now()}`,
        is_active: editingCategory?.is_active ?? true,
        created_at: editingCategory?.created_at || new Date().toISOString(),
      };

      if (editingCategory) {
        // Update existing category
        await JuntoApiService.updateClassCategory(categoryData as ClassCategory);
      } else {
        // Create new category
        await JuntoApiService.createClassCategory(categoryData);
      }
      
      await loadCategories();
      closeDialog();
    } catch (err) {
      console.error('Failed to save category:', err);
      setDialogError('Failed to save category. Please check your input.');
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading categories...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Class Categories</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
              />
            }
            label="Show Inactive"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow 
                  key={category.id} 
                  hover 
                  sx={{ 
                    opacity: category.is_active ? 1 : 0.6,
                    backgroundColor: category.is_active ? 'inherit' : 'action.hover'
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {category.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={category.event_type.toUpperCase()} 
                      color={
                        category.event_type === 'group' ? 'primary' :
                        category.event_type === 'private' ? 'secondary' :
                        category.event_type === 'event' ? 'warning' : 'success'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{category.description}</TableCell>
                  <TableCell>
                    {new Date(category.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={category.is_active ? "Active" : "Inactive"}
                      color={category.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={category}
                      entityType="class-categories"
                      entityDisplayName="Class Category"
                      onEdit={openEditDialog}
                      onRefresh={loadCategories}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Event Type:</strong> {entity.event_type}
                          </Typography>
                          {entity.description && (
                            <Typography variant="body1">
                              <strong>Description:</strong> {entity.description}
                            </Typography>
                          )}
                          <Typography variant="body1">
                            <strong>Active:</strong> {entity.is_active ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Class Category' : 'Create New Class Category'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.event_type}
                  label="Event Type"
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value as 'group' | 'private' | 'event' | 'wellness' })}
                >
                  <MenuItem value="group">Group Classes</MenuItem>
                  <MenuItem value="private">Private Sessions</MenuItem>
                  <MenuItem value="event">Special Events</MenuItem>
                  <MenuItem value="wellness">Wellness Services</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassCategoriesPage;